import React, { useEffect, useRef, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { bindActionCreators } from "redux";
import { saveAs } from "file-saver";

import { getBlobFile } from "../../api";
import { actionCreators, AppState } from "../../store";
import ImagePreviewModal from "../modals/ImagePreviewModal";
import type { ReactionsType } from "./Reactions";
import MessageMedia from "./MessageMedia";
import { ReduxConversation } from "../../store/reducers/convoReducer";
import {
  ReduxMedia,
  ReduxMessage,
} from "../../store/reducers/messageListReducer";
import {
  getSdkMediaObject,
  getSdkMessageObject,
  getSdkParticipantObject,
} from "../../conversations-objects";
import { getSdkConversationObject } from "../../conversations-objects";
import { ReduxParticipant } from "../../store/reducers/participantsReducer";
import Reactions from "./Reactions";
import { IosMessageStatus } from "./MessageStatus";
import { chatTimestamp, needsTimestamp } from "../../utils/iosTime";
import { useDropzone } from "react-dropzone";
import { MAX_FILE_SIZE } from "../../constants";

interface MessageListProps {
  messages: ReduxMessage[];
  conversation: ReduxConversation;
  participants: ReduxParticipant[];
  lastReadIndex: number;
  use24hTimeFormat: boolean;
  handleDroppedFiles: (droppedFiles: File[]) => void;
}

const MessageList: React.FC<MessageListProps> = (props: MessageListProps) => {
  const {
    messages,
    conversation,
    lastReadIndex,
    use24hTimeFormat,
    handleDroppedFiles,
  } = props;
  if (messages === undefined) {
    return <div className="empty" />;
  }

  // const theme = useTheme();
  const myRef = useRef<HTMLInputElement>(null);

  const dispatch = useDispatch();
  const { addAttachment, addNotifications, updateUser } = bindActionCreators(
    actionCreators,
    dispatch
  );
  const conversationAttachments = useSelector(
    (state: AppState) => state.attachments[conversation.sid]
  );
  const users = useSelector((state: AppState) => state.users);

  const [imagePreview, setImagePreview] = useState<{
    message: ReduxMessage;
    file: Blob;
    sid: string;
  } | null>(null);

  const [horizonMessageCount, setHorizonMessageCount] = useState<number>(0);
  // const [showHorizonIndex, setShowHorizonIndex] = useState<number>(0);
  const [scrolledToHorizon, setScrollToHorizon] = useState(false);
  const [files, setFiles] = useState<File[]>([]);

  const onDrop = useCallback((acceptedFiles) => {
    setFiles(acceptedFiles);
  }, []);
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    noClick: true,
    noKeyboard: true,
    maxSize: MAX_FILE_SIZE,
    accept: {
      "image/*": [".png", ".jpeg", ".jpg", ".gif"],
    },
  });

  useEffect(() => {
    if (scrolledToHorizon || !myRef.current) {
      return;
    }
    myRef.current.scrollIntoView({
      behavior: "smooth",
    });
    setScrollToHorizon(true);
  });

  useEffect(() => {
    if (lastReadIndex === -1 || horizonMessageCount) {
      return;
    }
    // const showIndex = 0;
    getSdkConversationObject(conversation)
      .getUnreadMessagesCount()
      .then((count) => {
        setHorizonMessageCount(count ?? 0);
        // setShowHorizonIndex(showIndex);
      });
  }, [messages, lastReadIndex]);

  // Updates the user list based on message authors to be able to get friendly names
  useEffect(() => {
    messages.forEach((message) => {
      const participant = message.participantSid
        ? participantsBySid.get(message.participantSid)
        : null;
      if (participant && participant.identity) {
        if (!users[participant.identity]) {
          const sdkParticipant = getSdkParticipantObject(participant);
          sdkParticipant.getUser().then((sdkUser) => {
            updateUser(sdkUser);
          });
        }
      }
    });
  }, [messages]);

  useEffect(() => {
    const abortController = new AbortController();
    handleDroppedFiles(files);
    return () => {
      abortController.abort();
    };
  }, [files]);

  const onDownloadAttachments = async (message: ReduxMessage) => {
    const attachedMedia = message.attachedMedia?.map(getSdkMediaObject);
    if (message.index === -1) {
      return undefined;
    }
    if (!attachedMedia?.length) {
      return new Error("No media attached");
    }

    for (const media of attachedMedia) {
      const blob = await getBlobFile(media, addNotifications);
      addAttachment(props.conversation.sid, message.sid, media.sid, blob);
    }

    return;
  };

  const onFileOpen = (file: Blob, { filename }: ReduxMedia) => {
    saveAs(file, filename ?? "");
  };

  const participantsBySid = new Map(props.participants.map((p) => [p.sid, p]));

  const getAuthorFriendlyName = (message: ReduxMessage) => {
    const author = message.author ?? "";
    if (message.participantSid == null) return author;

    const participant = participantsBySid.get(message.participantSid);
    if (participant == null || participant.identity == null) return author;

    const user = users[participant.identity];
    return user?.friendlyName || author;
  };

  // Find current user's participant to determine if a message is outbound
  const currentUsername = localStorage.getItem("username");
  const currentUserParticipant = props.participants.find(
    (p) => p.identity === currentUsername
  );
  const isOutbound = (message: ReduxMessage) =>
    currentUserParticipant
      ? message.participantSid === currentUserParticipant.sid
      : message.author === currentUsername;
  const senderKey = (message: ReduxMessage) =>
    message.participantSid ?? message.author ?? "";
  const isGroupChat = props.participants.length > 2;
  const lastOutboundSid = [...messages].reverse().find(isOutbound)?.sid;

  return (
    <div
      {...getRootProps()}
      className={`ios-chatlog${isDragActive ? " dragging" : ""}`}
    >
      <input {...getInputProps()} />
      {messages.map((message, index) => {
        const messageImages: ReduxMedia[] = [];
        const messageFiles: ReduxMedia[] = [];
        (message.attachedMedia || []).forEach((file) => {
          const { contentType } = file;
          if (contentType.includes("image")) {
            messageImages.push(file);
            return;
          }
          messageFiles.push(file);
        });
        const attributes = message.attributes as Record<
          string,
          ReactionsType | undefined
        >;
        const hasReactions = Object.values(attributes.reactions ?? {}).some(
          (users) => users?.length
        );

        const previous = messages[index - 1];
        const next = messages[index + 1];
        const outbound = isOutbound(message);
        const showTimestamp = needsTimestamp(
          previous?.dateCreated,
          message.dateCreated
        );
        const groupStart =
          showTimestamp ||
          !previous ||
          senderKey(previous) !== senderKey(message);
        // The tail goes on the last bubble before the sender changes
        const tail =
          !next ||
          senderKey(next) !== senderKey(message) ||
          needsTimestamp(message.dateCreated, next.dateCreated);
        const [day, time] = message.dateCreated
          ? chatTimestamp(message.dateCreated, use24hTimeFormat)
          : ["", ""];
        const hasMedia = messageImages.length + messageFiles.length > 0;

        return (
          <React.Fragment key={message.sid}>
            {showTimestamp && message.dateCreated && (
              <div className="ios-timestamp">
                <b>{day}</b> {time}
              </div>
            )}
            {!outbound && isGroupChat && groupStart && (
              <div className="ios-sender">{getAuthorFriendlyName(message)}</div>
            )}
            <div
              className={`ios-msg ${outbound ? "out" : "in"}${
                groupStart ? " group-start" : ""
              }${tail ? " tail" : ""}`}
              title={`${getAuthorFriendlyName(message)} · ${day} ${time}`}
            >
              <div className="ios-msg-row">
                <div
                  className={`ios-bubble${
                    hasMedia && !message.body ? " media-only" : ""
                  }`}
                >
                  {message.body}
                  {hasMedia && (
                    <div className="ios-media">
                      <MessageMedia
                        key={message.sid}
                        attachments={conversationAttachments?.[message.sid]}
                        onDownload={async () =>
                          await onDownloadAttachments(message)
                        }
                        images={messageImages}
                        files={messageFiles}
                        sending={message.index === -1}
                        onOpen={(
                          mediaSid: string,
                          image?: ReduxMedia,
                          file?: ReduxMedia
                        ) => {
                          if (file) {
                            onFileOpen(
                              conversationAttachments?.[message.sid][mediaSid],
                              file
                            );
                            return;
                          }
                          if (image) {
                            setImagePreview({
                              message,
                              file: conversationAttachments?.[message.sid][
                                mediaSid
                              ],
                              sid: mediaSid,
                            });
                          }
                        }}
                      />
                    </div>
                  )}
                </div>
                <div
                  className={`ios-reactions${
                    hasReactions ? " has-reactions" : ""
                  }`}
                >
                  <Reactions
                    reactions={attributes.reactions}
                    onReactionsChanged={(reactions) => {
                      getSdkMessageObject(message).updateAttributes({
                        ...attributes,
                        reactions,
                      });
                    }}
                  />
                </div>
              </div>
              {outbound && (
                <IosMessageStatus
                  message={message}
                  channelParticipants={props.participants}
                  isLast={message.sid === lastOutboundSid}
                />
              )}
            </div>
          </React.Fragment>
        );
      })}
      {imagePreview
        ? (function () {
            const dateString = imagePreview?.message.dateCreated;
            const date = dateString ? new Date(dateString) : "";
            return (
              <ImagePreviewModal
                image={imagePreview.file}
                isOpen={!!imagePreview}
                author={
                  imagePreview
                    ? getAuthorFriendlyName(imagePreview.message)
                    : ""
                }
                date={
                  date
                    ? date.toDateString() +
                      ", " +
                      date.getHours() +
                      ":" +
                      (date.getMinutes() < 10 ? "0" : "") +
                      date.getMinutes()
                    : ""
                }
                handleClose={() => setImagePreview(null)}
                onDownload={() => {
                  saveAs(
                    imagePreview.file,
                    imagePreview.message.attachedMedia?.find(
                      ({ sid }) => sid === imagePreview.sid
                    )?.filename ?? ""
                  );
                }}
              />
            );
          })()
        : null}
    </div>
  );
};

export default MessageList;
